import { Link, createFileRoute } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { AngaMark } from "@/components/brand/AngaMark";
import { AngaWordmark } from "@/components/brand/AngaWordmark";
import { Button } from "@/components/ui/button";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Meteo" },
      {
        name: "description",
        content: "Resident flood risk dashboard and emergency operations centre.",
      },
      { property: "og:title", content: "Local flood alerts" },
      {
        property: "og:description",
        content: "A clear, local view of flood risk for residents and emergency operations teams.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PortalPage,
});

function PortalPage() {
  const { platformName } = usePlatformConfig();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Atmosphere — full page, no frame */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%),linear-gradient(180deg,var(--map)_0%,var(--background)_58%)]" />
        <div className="map-grid absolute inset-0 opacity-25" />
        <WeatherScene />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-10 md:px-10">
        <header className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#064E3B] text-white">
              <AngaMark className="h-6 w-6" title={platformName} />
            </div>
            <div className="min-w-0">
              <AngaWordmark className="text-xl" />
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Early warning network
              </p>
            </div>
          </div>
          <nav className="flex shrink-0 items-center gap-2" aria-label="Account">
            <Button variant="outline" size="sm" asChild>
              <Link to="/login">Sign in</Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/signup">Sign up</Link>
            </Button>
          </nav>
        </header>

        <main className="flex flex-1 flex-col justify-center py-12 md:py-16">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
            Early warning
          </p>
          <h1 className="max-w-2xl font-display text-4xl font-bold tracking-normal md:text-5xl">
            {platformName}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
            Flood and weather advisories for where you are and where you are going — sign in or
            create an account to get started.
          </p>
        </main>

        <footer className="border-t border-border/60 pt-6 text-xs text-muted-foreground">
          <p>
            Advisories are not official government warnings. Follow emergency services if guidance
            differs.
          </p>
          <p className="mt-3">
            <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">
              Privacy policy
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
}

function WeatherScene() {
  const clouds = [
    {
      track: "anga-cloud-track-a",
      cloud: "anga-cloud-a",
      top: "8%",
      width: "min(520px,48vw)",
      opacity: 0.88,
      delay: "0s",
    },
    {
      track: "anga-cloud-track-b",
      cloud: "anga-cloud-b",
      top: "22%",
      width: "min(420px,40vw)",
      opacity: 0.65,
      delay: "-8s",
    },
    {
      track: "anga-cloud-track-c",
      cloud: "anga-cloud-c",
      top: "42%",
      width: "min(360px,34vw)",
      opacity: 0.5,
      delay: "-16s",
    },
    {
      track: "anga-cloud-track-a",
      cloud: "anga-cloud-a",
      top: "58%",
      width: "min(480px,44vw)",
      opacity: 0.72,
      delay: "-22s",
    },
    {
      track: "anga-cloud-track-b",
      cloud: "anga-cloud-b",
      top: "72%",
      width: "min(300px,28vw)",
      opacity: 0.4,
      delay: "-5s",
    },
    {
      track: "anga-cloud-track-c",
      cloud: "anga-cloud-c",
      top: "14%",
      width: "min(280px,26vw)",
      opacity: 0.35,
      delay: "-12s",
    },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#cfe6dc]/40 via-transparent to-transparent" />

      {clouds.map((cloud, i) => (
        <div
          key={i}
          className={`anga-cloud-track ${cloud.track}`}
          style={{
            top: cloud.top,
            width: cloud.width,
            opacity: cloud.opacity,
            animationDelay: cloud.delay,
          }}
        >
          <CloudShape
            id={`c${i}`}
            className={`anga-cloud ${cloud.cloud}`}
            style={{ animationDelay: cloud.delay }}
          />
        </div>
      ))}

      <LightningBolt
        className="anga-bolt anga-bolt-1 left-[62%] top-[6%] h-[38%] w-auto"
        fill="#34D399"
        stroke="#059669"
        glow="rgb(52 211 153 / 0.65)"
      />
      <LightningBolt
        className="anga-bolt anga-bolt-2 left-[28%] top-[18%] h-[28%] w-auto"
        fill="#FBBF24"
        stroke="#D97706"
        glow="rgb(251 191 36 / 0.7)"
      />
      <LightningBolt
        className="anga-bolt anga-bolt-3 right-[12%] top-[10%] h-[32%] w-auto"
        fill="#38BDF8"
        stroke="#0284C7"
        glow="rgb(56 189 248 / 0.65)"
      />

      <div className="anga-sky-flash absolute inset-0" />
      <div className="anga-rain absolute inset-0 opacity-40" />
    </div>
  );
}

function CloudShape({
  id,
  className,
  style,
}: {
  id: string;
  className?: string;
  style?: CSSProperties;
}) {
  const gradId = `cloudSoft-${id}`;
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 240 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M60 78c-22 0-40-14-40-32 0-16 12-29 28-32 6-18 24-30 44-30 22 0 41 14 46 34 4-2 9-3 14-3 18 0 32 12 32 28 0 2 0 4-1 6 14 4 24 15 24 29 0 17-16 30-35 30H60Z"
        fill="white"
        fillOpacity="0.92"
      />
      <path
        d="M60 78c-22 0-40-14-40-32 0-16 12-29 28-32 6-18 24-30 44-30 22 0 41 14 46 34 4-2 9-3 14-3 18 0 32 12 32 28 0 2 0 4-1 6 14 4 24 15 24 29 0 17-16 30-35 30H60Z"
        fill={`url(#${gradId})`}
        fillOpacity="0.35"
      />
      <defs>
        <linearGradient id={gradId} x1="40" y1="20" x2="200" y2="90" gradientUnits="userSpaceOnUse">
          <stop stopColor="#064E3B" stopOpacity="0" />
          <stop offset="1" stopColor="#064E3B" stopOpacity="0.12" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function LightningBolt({
  className,
  fill,
  stroke,
  glow,
}: {
  className?: string;
  fill: string;
  stroke: string;
  glow: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: `drop-shadow(0 0 12px ${glow})` }}
    >
      <path d="M22 2 L8 52 H18 L10 118 L34 42 H22 L30 2 Z" fill={fill} fillOpacity="0.95" />
      <path
        d="M22 2 L8 52 H18 L10 118 L34 42 H22 L30 2 Z"
        stroke={stroke}
        strokeWidth="2"
        strokeOpacity="0.9"
      />
    </svg>
  );
}
